import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import {
  LuX,
  LuPlay,
  LuPause,
  LuMousePointerClick,
  LuClapperboard,
  LuFolder,
  LuArrowLeft,
  LuWandSparkles,
  LuChevronDown,
  LuChevronUp,
  LuRepeat,
  LuTrash2,
} from "react-icons/lu";
import IdealStudio from "../../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../../common/constants";
import { randomPositionGenerator } from "../../../clipboard/clipboard.utils";
import { createBox } from "../../../../../common/common_tools";
import { addSound } from "../../../../../common/actions";
import {
  generateSoundEffect,
  fetchOrgSoundEffects,
  deleteIdealStudioFile,
} from "../../../../../common/server_utils.es6";
import {
  fetchSoundLibrary,
  filterSounds,
  getDisplayName,
  getCategoryLabel,
} from "../data/soundLibraryData";
import PanelSearch from "../ui/PanelSearch";
import EmptyState from "../ui/EmptyState";
import "./_panel.scss";

const TYPE_CHIPS = [
  { id: null, i18nKey: "sounds_all", fallback: "All" },
  { id: "sfx", i18nKey: "sounds_sfx", fallback: "Sound Effects" },
  { id: "music", i18nKey: "sounds_music", fallback: "Music" },
  { id: "jingle", i18nKey: "sounds_jingles", fallback: "Jingles" },
  // Org-scoped AI-generated effects (ElevenLabs). Not a manifest type — a
  // separate source that swaps the panel into generate + list mode.
  { id: "my-effects", i18nKey: "sounds_my_effects", fallback: "Efektlerim" },
];

const DURATION_CHIPS = [
  { id: null, i18nKey: "all", fallback: "All" },
  { id: "short", i18nKey: "sounds_dur_short", fallback: "Short" },
  { id: "medium", i18nKey: "sounds_dur_medium", fallback: "Medium" },
  { id: "long", i18nKey: "sounds_dur_long", fallback: "Long" },
];

// Sound-effect generate presets — chip options (fxDuration is kept as a string,
// "" = auto). Mirrors the panel's pill vocabulary instead of native inputs.
const SFX_DURATION_PRESETS = ["", "3", "5", "10", "15"];
const SFX_INFLUENCE_PRESETS = [
  { val: 0.2, i18nKey: "sound_fx_low", fallback: "Düşük" },
  { val: 0.3, i18nKey: "sound_fx_mid", fallback: "Orta" },
  { val: 0.6, i18nKey: "sound_fx_high", fallback: "Yüksek" },
];
const SFX_COUNT_PRESETS = [1, 2, 3, 4];

// Pill/chip style shared by the effect form controls — matches the panel's
// existing TYPE_CHIPS look (indigo accent #4f46e5, #eef2ff active fill).
const sfxPill = (active) => ({
  padding: "5px 12px",
  borderRadius: "14px",
  border: "1px solid " + (active ? "#4f46e5" : "#e5e7eb"),
  background: active ? "#eef2ff" : "#fff",
  color: active ? "#4338ca" : "#374151",
  fontSize: "12px",
  fontWeight: active ? 600 : 500,
  cursor: "pointer",
  lineHeight: 1.4,
});
const sfxFieldLabel = {
  fontSize: "11px",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  marginBottom: "6px",
};

function formatDuration(sec) {
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function ChipRow({ chips, active, onSelect }) {
  return (
    <div className="cmp-panel__toolbar" style={{ gap: "6px", flexWrap: "wrap" }}>
      {chips.map((chip) => (
        <button
          key={chip.id || "all"}
          type="button"
          onClick={() => onSelect(chip.id)}
          style={{
            padding: "4px 10px",
            borderRadius: "14px",
            border: "1px solid " + (active === chip.id ? "#4f46e5" : "#e5e7eb"),
            background: active === chip.id ? "#eef2ff" : "#fff",
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          {i18n.t(chip.i18nKey) || chip.fallback}
        </button>
      ))}
    </div>
  );
}

ChipRow.propTypes = {
  chips: PropTypes.array.isRequired,
  active: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
};

// Sound-asset panel for the Components module: each item can be added as an
// AudioCue box (click-to-play cue) or appended to the scene's soundList.
// Default view is folder-first (one card per manifest category); clicking a
// folder scopes the row list to that category. A non-empty search overrides
// both with a flat, cross-category result list — no refetch, pure state swap.
function SoundItemsPanel({
  onBoxAdded,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  onClose,
}) {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [durationFilter, setDurationFilter] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  // Id of the item the live Audio element belongs to — kept across pause so
  // resuming (or seeking) the same row continues from its last position.
  const activeItemIdRef = useRef(null);

  // "Efektlerim" (My Effects) mode: org-scoped AI-generated sound effects.
  const myEffectsMode = typeFilter === "my-effects";
  const [effects, setEffects] = useState([]);
  const [effectsLoading, setEffectsLoading] = useState(false);
  const [effectsError, setEffectsError] = useState(false);
  // Generate form
  const [fxPrompt, setFxPrompt] = useState("");
  const [fxEnrich, setFxEnrich] = useState(false);
  const [fxDuration, setFxDuration] = useState(""); // "" = auto
  const [fxInfluence, setFxInfluence] = useState(0.3);
  const [fxLoop, setFxLoop] = useState(false);
  const [fxCount, setFxCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [fxFormOpen, setFxFormOpen] = useState(false); // create form collapsed/expanded (default closed)
  const [deletingId, setDeletingId] = useState(null); // effect currently being deleted

  useEffect(() => {
    let cancelled = false;
    fetchSoundLibrary()
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const searching = searchTerm.trim() !== "";

  // Manifest sounds + org AI effects, merged so effects show up under "All"
  // (and the SFX filter) as a "my-effects" folder — not only the "Efektlerim" tab.
  const allItems = useMemo(() => [...items, ...effects], [items, effects]);

  // Folder cards for the current type filter — derived dynamically from the
  // merged list, not hardcoded. Hidden while searching or once a category (or
  // search) is active.
  const folders = useMemo(() => {
    if (searching || selectedCategory) return [];
    const counts = new Map();
    allItems
      .filter((it) => !typeFilter || it.type === typeFilter)
      .forEach((it) => counts.set(it.category, (counts.get(it.category) || 0) + 1));
    return Array.from(counts.entries()).map(([category, count]) => ({ category, count }));
  }, [allItems, typeFilter, searching, selectedCategory]);

  const rows = useMemo(() => {
    if (searching) {
      return filterSounds(allItems, {
        type: typeFilter,
        search: searchTerm,
        duration: durationFilter,
      });
    }
    if (selectedCategory) {
      return filterSounds(allItems, {
        type: typeFilter,
        category: selectedCategory,
        duration: durationFilter,
      });
    }
    return [];
  }, [allItems, typeFilter, searchTerm, searching, selectedCategory, durationFilter]);

  const showFolders = !searching && !selectedCategory && !myEffectsMode;
  const showDurationChips = !showFolders && !myEffectsMode;

  // Loads item.url into a fresh Audio element (no metadata preload — total
  // duration always comes from the manifest) and plays it from atTime.
  const startItem = (item, atTime = 0) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(item.url);
    if (atTime) audio.currentTime = atTime;
    audio.onended = () => {
      setPlayingId(null);
      setCurrentTime(0);
    };
    audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
    audioRef.current = audio;
    activeItemIdRef.current = item.id;
    audio.play().catch(() => {});
    setPlayingId(item.id);
    setCurrentTime(atTime);
  };

  const togglePlay = (item) => {
    if (playingId === item.id) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    if (activeItemIdRef.current === item.id && audioRef.current) {
      audioRef.current.play().catch(() => {});
      setPlayingId(item.id);
      return;
    }
    startItem(item);
  };

  const seek = (item, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const time = ratio * item.durationSec;
    if (activeItemIdRef.current === item.id && audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
      if (playingId !== item.id) {
        audioRef.current.play().catch(() => {});
        setPlayingId(item.id);
      }
      return;
    }
    startItem(item, time);
  };

  // Action (a): AudioCue box — navItem/parent/inASlide logic from ImageItemsPanel,
  // timeline block from MyFilesPanel; durationSec comes from the manifest (no probe).
  const addAsCueBox = (item) => {
    if (!(onBoxAdded && boxes && navItems && containedViews)) return;
    const pluginName = "AudioCue";
    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) return;
    const navItem =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];
    if (!navItem) return;
    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);
    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    const FPS = 30;

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      componentType: "Sounds",
      position: {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      },
      id: ID_PREFIX_BOX + Date.now(),
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      url: item.url,
      // addBoxThunk owns placement (appends onto the shared audio row).
      timeline: { durationHintFrames: Math.ceil(item.durationSec * FPS), visible: true },
    };
    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  // Action (b): scene sound — same soundList path narration uses.
  const addAsSceneSound = (item) => {
    if (!navItemSelected) return;
    dispatch(
      addSound(navItemSelected, {
        file: item.url,
        name: getDisplayName(item),
        startTime: 0,
        endTime: item.durationSec,
        volumeLevel: 1,
      })
    );
  };

  // Map an org sound-effect file record to the row shape renderRow expects.
  const mapEffect = (f) => ({
    id: f.id,
    url: f.fileVirtualPath,
    displayName: f.fileDescription || f.prompt || f.fileName || "Efekt",
    category: "my-effects",
    // Treated as an SFX source so it shows under "All" and the SFX filter.
    type: "sfx",
    durationSec: Number(f.fileDuration) || 0,
  });

  const loadEffects = () => {
    setEffectsLoading(true);
    setEffectsError(false);
    return fetchOrgSoundEffects({})
      .then((list) => setEffects((Array.isArray(list) ? list : []).map(mapEffect)))
      .catch(() => setEffectsError(true))
      .finally(() => setEffectsLoading(false));
  };

  // Load the org effect list on mount (so effects show under "All") and refresh
  // it when entering "Efektlerim".
  useEffect(() => {
    loadEffects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEffectsMode]);

  const handleGenerate = async () => {
    const text = fxPrompt.trim();
    if (!text || generating) return;
    setGenerating(true);
    setGenError("");
    try {
      const res = await generateSoundEffect({
        text,
        // enrich uses the middleware's built-in sound-design system prompt —
        // not user-supplied.
        enrich: fxEnrich,
        durationSeconds: fxDuration === "" ? null : Number(fxDuration),
        promptInfluence: Number(fxInfluence),
        loop: fxLoop,
        count: Number(fxCount) || 1,
      });
      if (!res || res.success === false) {
        setGenError((res && res.error) || i18n.t("sound_fx_error") || "Efekt oluşturulamadı");
      } else {
        // New effects are persisted server-side — refetch so they show in the list.
        await loadEffects();
      }
    } catch (e) {
      setGenError(i18n.t("sound_fx_error") || "Efekt oluşturulamadı");
    } finally {
      setGenerating(false);
    }
  };

  // Delete an org effect (irreversible). Removes the file record server-side,
  // then drops the row optimistically. Only effect rows expose this.
  const deleteEffect = async (item) => {
    if (deletingId) return;
    if (
      !window.confirm(
        i18n.t("sound_fx_delete_confirm") || "Bu efekti silmek istediğine emin misin?"
      )
    )
      return;
    setDeletingId(item.id);
    try {
      await deleteIdealStudioFile(item.id);
      if (activeItemIdRef.current === item.id && audioRef.current) {
        audioRef.current.pause();
        setPlayingId(null);
      }
      setEffects((prev) => prev.filter((e) => e.id !== item.id));
    } catch (e) {
      setGenError(i18n.t("sound_fx_delete_error") || "Efekt silinemedi");
    } finally {
      setDeletingId(null);
    }
  };

  // Client-side search over the loaded effects (org list already fetched).
  const effectRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return effects;
    return effects.filter((e) => (e.displayName || "").toLowerCase().includes(q));
  }, [effects, searchTerm]);

  // Row markup shared by the list and search views — identical between the two.
  const renderRow = (item) => {
    const name = getDisplayName(item);
    const isActiveRow = activeItemIdRef.current === item.id;
    const isPlaying = playingId === item.id;
    const shownTime = isActiveRow ? currentTime : 0;
    const progressPct = Math.min(100, (shownTime / (item.durationSec || 1)) * 100);
    return (
      <div
        key={item.id}
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "10px 12px",
        }}
      >
        <div
          title={name}
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#111827",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: "11px",
            color: "#9ca3af",
            marginBottom: "8px",
          }}
        >
          {getCategoryLabel(item.category)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            type="button"
            onClick={() => togglePlay(item)}
            title={i18n.t("preview")}
            style={{
              flexShrink: 0,
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              border: "none",
              background: "#4f46e5",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {isPlaying ? (
              <LuPause size={13} strokeWidth={2.25} />
            ) : (
              <LuPlay size={13} strokeWidth={2.25} />
            )}
          </button>

          <div
            onClick={(e) => seek(item, e)}
            style={{
              flex: 1,
              height: "6px",
              borderRadius: "3px",
              background: "#e5e7eb",
              position: "relative",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: `${progressPct}%`,
                borderRadius: "3px",
                background: "#4f46e5",
              }}
            />
          </div>

          <span
            style={{
              flexShrink: 0,
              fontSize: "11px",
              color: "#6b7280",
              minWidth: "72px",
              textAlign: "right",
            }}
          >
            {formatDuration(shownTime)} / {formatDuration(item.durationSec)}
          </span>

          <button
            type="button"
            onClick={() => addAsCueBox(item)}
            title={i18n.t("sound_add_cue") || "Add as click sound"}
            style={{
              flexShrink: 0,
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <LuMousePointerClick size={14} strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={() => addAsSceneSound(item)}
            title={i18n.t("sound_add_scene") || "Add as scene sound"}
            style={{
              flexShrink: 0,
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#6b7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <LuClapperboard size={14} strokeWidth={2.25} />
          </button>
          {item.category === "my-effects" && (
            <button
              type="button"
              onClick={() => deleteEffect(item)}
              disabled={deletingId === item.id}
              title={i18n.t("delete") || "Sil"}
              style={{
                flexShrink: 0,
                width: "28px",
                height: "28px",
                borderRadius: "8px",
                border: "1px solid #fecaca",
                background: "#fff",
                color: "#ef4444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: deletingId === item.id ? "default" : "pointer",
                opacity: deletingId === item.id ? 0.5 : 1,
              }}
            >
              <LuTrash2 size={14} strokeWidth={2.25} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="cmp-panel">
      <div className="cmp-panel__toolbar">
        <PanelSearch
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm("")}
          placeholder={i18n.t("search") || "Search"}
        />
        {onClose && (
          <button
            type="button"
            className="cmp-panel__close"
            onClick={onClose}
            title={i18n.t("close")}
          >
            <LuX size={15} />
          </button>
        )}
      </div>

      <ChipRow chips={TYPE_CHIPS} active={typeFilter} onSelect={setTypeFilter} />
      {showDurationChips && (
        <ChipRow chips={DURATION_CHIPS} active={durationFilter} onSelect={setDurationFilter} />
      )}

      {!searching && selectedCategory && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "0 10px",
            marginBottom: "12px",
          }}
        >
          <button
            type="button"
            onClick={() => setSelectedCategory(null)}
            title={i18n.t("sounds_back") || "Back"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: "#4f46e5",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <LuArrowLeft size={15} strokeWidth={2.25} />
          </button>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
            {getCategoryLabel(selectedCategory)}
          </span>
        </div>
      )}

      <div className="cmp-panel__scroll">
        {myEffectsMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {/* Generate form */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <button
                type="button"
                onClick={() => setFxFormOpen((o) => !o)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {i18n.t("sound_fx_new") || "Create Effect"}
                </span>
                {fxFormOpen ? (
                  <LuChevronUp size={16} color="#6b7280" />
                ) : (
                  <LuChevronDown size={16} color="#6b7280" />
                )}
              </button>

              {fxFormOpen && (
                <>
                  <textarea
                    value={fxPrompt}
                    onChange={(e) => setFxPrompt(e.target.value)}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#4f46e5";
                      e.target.style.boxShadow = "0 0 0 3px rgba(79,70,229,0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e5e7eb";
                      e.target.style.boxShadow = "none";
                    }}
                    placeholder={
                      i18n.t("sound_fx_prompt_ph") ||
                      "Efekt açıklaması (ör. boş taş salonda kapanan ağır ahşap kapı)"
                    }
                    rows={3}
                    style={{
                      width: "100%",
                      resize: "vertical",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      fontSize: "13px",
                      fontFamily: "inherit",
                      color: "#111827",
                      outline: "none",
                      boxSizing: "border-box",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                    }}
                  />

                  {/* Toggles — enrich + loop grouped on one row */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    <button
                      type="button"
                      onClick={() => setFxEnrich((v) => !v)}
                      style={{
                        ...sfxPill(fxEnrich),
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <LuWandSparkles size={13} color={fxEnrich ? "#4f46e5" : "#9ca3af"} />
                      {i18n.t("sound_fx_enrich") || "AI ile zenginleştir"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFxLoop((v) => !v)}
                      style={{
                        ...sfxPill(fxLoop),
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <LuRepeat size={13} color={fxLoop ? "#4f46e5" : "#9ca3af"} />
                      {i18n.t("sound_fx_loop") || "Döngü"}
                    </button>
                  </div>

                  {/* Süre — full-width pill row */}
                  <div>
                    <div style={sfxFieldLabel}>{i18n.t("sound_fx_duration") || "Süre"}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {SFX_DURATION_PRESETS.map((sec) => (
                        <button
                          key={sec || "auto"}
                          type="button"
                          onClick={() => setFxDuration(sec)}
                          style={sfxPill(fxDuration === sec)}
                        >
                          {sec === "" ? i18n.t("auto") || "Oto" : `${sec}s`}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt etkisi + Adet — paired side by side */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    <div style={{ flex: "1 1 150px" }}>
                      <div style={sfxFieldLabel}>
                        {i18n.t("sound_fx_influence") || "Prompt etkisi"}
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {SFX_INFLUENCE_PRESETS.map((p) => (
                          <button
                            key={p.val}
                            type="button"
                            onClick={() => setFxInfluence(p.val)}
                            style={sfxPill(Number(fxInfluence) === p.val)}
                          >
                            {i18n.t(p.i18nKey) || p.fallback}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div style={sfxFieldLabel}>{i18n.t("sound_fx_count") || "Adet"}</div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {SFX_COUNT_PRESETS.map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setFxCount(n)}
                            style={{ ...sfxPill(Number(fxCount) === n), minWidth: "36px" }}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={generating || !fxPrompt.trim()}
                    className="app-modal__btn app-modal__btn--primary"
                    style={{ width: "100%", marginTop: "2px" }}
                  >
                    {generating
                      ? i18n.t("sound_fx_generating") || "Oluşturuluyor…"
                      : i18n.t("sound_fx_generate") || "Efekt Oluştur"}
                  </button>

                  {genError && <div style={{ fontSize: "12px", color: "#ef4444" }}>{genError}</div>}
                </>
              )}
            </div>

            {/* Effect list */}
            {effectsLoading ? (
              <EmptyState title={i18n.t("loading") || "Yükleniyor…"} hint="" />
            ) : effectsError ? (
              <EmptyState title={i18n.t("files_load_error") || "Efektler yüklenemedi"} hint="" />
            ) : effectRows.length === 0 ? (
              <EmptyState
                title={i18n.t("sound_fx_empty") || "Henüz efekt yok"}
                hint={i18n.t("sound_fx_empty_hint") || "Yukarıdan ilk efektini oluştur"}
              />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {effectRows.map(renderRow)}
              </div>
            )}
          </div>
        ) : loading ? (
          <EmptyState title="…" hint="" />
        ) : error ? (
          <EmptyState title={i18n.t("files_load_error") || "Sesler yüklenemedi"} hint="" />
        ) : showFolders ? (
          folders.length === 0 ? (
            <EmptyState title={i18n.t("no_results")} hint={i18n.t("try_other_search")} />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "8px",
              }}
            >
              {folders.map(({ category, count }) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "8px",
                    padding: "12px 10px",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: "#ede9fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LuFolder size={17} strokeWidth={1.8} color="#7c3aed" />
                  </span>
                  <span style={{ width: "100%", minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#111827",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getCategoryLabel(category)}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "2px",
                        display: "block",
                      }}
                    >
                      {count} {i18n.t("files") || "dosya"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )
        ) : rows.length === 0 ? (
          <EmptyState title={i18n.t("no_results")} hint={i18n.t("try_other_search")} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {rows.map(renderRow)}
          </div>
        )}
      </div>
    </div>
  );
}

SoundItemsPanel.propTypes = {
  onBoxAdded: PropTypes.func,
  boxes: PropTypes.object,
  navItems: PropTypes.object,
  navItemSelected: PropTypes.any,
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.any,
  onClose: PropTypes.func,
};

export default SoundItemsPanel;
